/* eslint-disable import/no-duplicates */
import { useMutation, useQuery } from '@apollo/client';
import { InvoiceTransactionListTable } from '@nile/client_management/src/Pages/Invoice/InvoiceTransactionListTable';
import { WarningRecordPayment } from '@nile/client_management/src/Pages/Invoice/RecordPayment';
import {
  RecordPayment,
  useRecordPaymentContext
} from '@nile/client_management/src/Pages/Invoice/RecordPayment.tsx';
import {
  getRemainingBalance,
  useCredits
} from '@nile/client_management/src/Utils/invoiceAndQuoteHelper';
import {
  Column,
  CustomModal,
  Loader,
  Row,
  getParsedCrudData,
  toast,
  COMMON_CONSTANTS,
  PSA_CONSTANTS
} from '@nile/components';
import { UPDATE_PAYMENT } from '@nile/graphql/src/Mutation/partialPayments';
import { GET_PAYMENT } from '@nile/graphql/src/Query/client_management/partialPayments';
import { AccountClient } from '@nile/graphql';
import { SVGIcon } from '@superops/super-icons';
import {
  Banner,
  Button,
  DisplayText,
  Page,
  TextStyle
} from '@superops/ui-design-system';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { omit, pick, isEmpty } from 'lodash';
import { getDifference } from '@nile/banyan/src/Pebbles/securityConfig';

function EditPaymentModalBase({ open, onClose, data, isLoading, onSuccess }) {
  const {
    validate,
    amount,
    dateOfPayment,
    paymentMethod,
    reference,
    setAmount,
    setDateOfPayment,
    setReference,
    setPaymentMethod,
    invoiceTotal
  } = useRecordPaymentContext();

  const [showWarning, setShowWarning] = React.useState(false);

  const [updatePayment, { loading: updatePaymentLoading }] = useMutation(
    UPDATE_PAYMENT,
    {
      client: AccountClient
    }
  );

  const [updatedTransaction, setUpdatedTransaction] = React.useState(
    data?.transactions || []
  );

  const {
    remainingBalance,
    isExcess,
    remainingAmountStatus
  } = getRemainingBalance({
    amountPaid: amount,
    invoiceTotal: updatedTransaction?.reduce(
      (total, transaction) => total + parseFloat(transaction.amount),
      0
    )
  });

  useEffect(() => {
    setAmount(data?.amount);
    setDateOfPayment(data?.paymentDate);
    setReference(data?.paymentReference);
    setPaymentMethod({
      ...data?.paymentMethod,
      value: data?.paymentMethod?.paymentMethodId,
      label: data?.paymentMethod?.paymentMethodName,
      name: data?.paymentMethod?.paymentMethodName
    });
    setUpdatedTransaction(data?.transactions ?? []);
  }, [data]);

  function updatePaymentMutation() {
    const { deletedItems, updatedItems } = getParsedCrudData(
      data?.transactions,
      updatedTransaction.map((t) => omit(t, ['error'])),
      'transactionId'
    );

    const originalInput = {
      amount,
      paymentDate: dateOfPayment,
      paymentMethod: {
        paymentMethodId: paymentMethod?.value
      },
      paymentReference: reference
    };

    const diffInput = getDifference(originalInput, {
      ...data,
      paymentMethod: pick(data?.paymentMethod, 'paymentMethodId')
    });

    const updateTransactions = updatedItems.map((t) =>
      pick(t, ['transactionId', 'amount'])
    );
    const deleteTransactions = deletedItems.map((t) =>
      pick(t, ['transactionId'])
    );

    diffInput.paymentId = data?.paymentId;

    if (updateTransactions?.length) {
      diffInput.updateTransactions = updateTransactions;
    }

    if (deleteTransactions?.length) {
      diffInput.deleteTransactions = deleteTransactions;
    }

    if (isEmpty(diffInput)) {
      return;
    }

    updatePayment({
      variables: {
        input: diffInput
      }
    })
      .then((res) => {
        if (res.data.updatePayment) {
          toast('success', 'invoice.partialPayment.updateToast');
          onSuccess();
        }
      })
      .finally(() => {
        onClose();
      });
  }

  return (
    <>
      {showWarning && (
        <WarningRecordPayment
          primaryAction={() => {
            setShowWarning(false);
            updatePaymentMutation();
          }}
          secondaryAction={() => {
            setShowWarning(false);
          }}
        />
      )}
      <CustomModal open={open} onClose={onClose} zIndex={1}>
        <div className="w900">
          {isLoading ? (
            <Loader />
          ) : (
            <Page
              variant="primary"
              contentHeight="120px"
              title={
                <TextStyle weight="Bold">
                  <FormattedMessage id="invoice.partialPayments.editPayment" />
                </TextStyle>
              }
              primaryAction={
                <Row gap={20}>
                  <Button primary onClick={onClose} plainSecondary>
                    <FormattedMessage id="common.cancel" />
                  </Button>
                  <Button
                    primary
                    onClick={async () => {
                      const result = await validate();
                      const hasError = updatedTransaction.some(
                        (transaction) => transaction.error
                      );
                      if (!result.isValid || hasError) return;
                      if (isExcess) {
                        setShowWarning(true);
                      } else {
                        updatePaymentMutation();
                      }
                    }}
                    loading={updatePaymentLoading}
                  >
                    <FormattedMessage id="invoice.partialPayment.updatePayment" />
                  </Button>
                </Row>
              }
            >
              <Column gap={40}>
                <Row gap={30}>
                  <RecordPayment.Form />
                  <div>
                    {remainingAmountStatus === PSA_CONSTANTS.POSITIVE &&
                      updatedTransaction?.some(
                        (transaction) => Number(transaction.amount) > 0
                      ) && (
                        <Banner status="purpleInfo" hideIcon>
                          <Row gap={10} className="items-center">
                            <SVGIcon name="Wallet" />
                            <FormattedMessage
                              id="invoice.partialPayments.excessAmountBanner"
                              values={{
                                b: (chunk) => (
                                  <DisplayText
                                    element="span"
                                    size="large"
                                    weight="Bold"
                                  >
                                    {chunk}
                                  </DisplayText>
                                )
                              }}
                            >
                              {(chunk) => <span>{chunk}</span>}
                            </FormattedMessage>
                          </Row>
                        </Banner>
                      )}
                    {updatedTransaction?.length > 0 &&
                      remainingAmountStatus === PSA_CONSTANTS.NEGATIVE && (
                        <Banner status="warning" hideIcon>
                          <Row gap={10} className="items-center">
                            <SVGIcon name="WarningTriangleOutline" />
                            <FormattedMessage id="invoice.partialPayments.lesserAmountBanner" />
                          </Row>
                        </Banner>
                      )}
                  </div>
                </Row>
                <div className="overflow-y-scroll">
                  <InvoiceTransactionListTable
                    transactions={updatedTransaction}
                    originalTransactions={data?.transactions}
                    isEditable
                    receivedAmount={amount}
                    onDelete={(row) => {
                      setUpdatedTransaction((tx) =>
                        tx.filter((t) => t.transactionId !== row.transactionId)
                      );
                    }}
                    onTransactionChange={(val) => {
                      setUpdatedTransaction(val);
                    }}
                  />
                </div>
              </Column>
            </Page>
          )}
        </div>
      </CustomModal>
    </>
  );
}

export function EditPaymentModal(props) {
  const { open, onClose, data } = props;
  const { hasCredits } = useCredits(data?.client?.accountId);

  const { data: paymentData, loading } = useQuery(GET_PAYMENT, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    variables: { input: { paymentId: data?.paymentId } },
    client: AccountClient
  });

  return (
    <RecordPayment
      invoiceTotal={paymentData?.getPayment?.transactions?.reduce(
        (total, transaction) => total + parseFloat(transaction.amount),
        0
      )}
      hasCredits={hasCredits}
      clientId={data.client.accountId}
      skipFetchingInvoiceSettings
    >
      <EditPaymentModalBase
        {...props}
        isLoading={loading}
        data={paymentData?.getPayment}
      />
    </RecordPayment>
  );
}
